
export interface QuizQuestion {
  id: number;
  question: string;
  options: {
    text: string;
    icon: string;
    profile: 'visionario' | 'guardiao' | 'explorador' | 'curador';
  }[];
}

export interface QuizProfile {
  id: 'visionario' | 'guardiao' | 'explorador' | 'curador';
  title: string;
  subtitle: string;
  description: string;
  traits: { icon: string; title: string; description: string }[];
  recommendations: string[];
  color: string;
  emoji: string;
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: 1,
    question: 'Quando você pensa no futuro, qual sentimento predomina?',
    options: [
      { text: 'Entusiasmo e possibilidades infinitas', icon: 'ri-sparkling-2-line', profile: 'visionario' },
      { text: 'Segurança e estabilidade emocional', icon: 'ri-shield-check-line', profile: 'guardiao' },
      { text: 'Curiosidade e vontade de explorar', icon: 'ri-compass-3-line', profile: 'explorador' },
      { text: 'Desejo de ajudar e transformar vidas', icon: 'ri-heart-pulse-line', profile: 'curador' },
    ],
  },
  {
    id: 2,
    question: 'Em um momento difícil, qual é sua primeira reação?',
    options: [
      { text: 'Visualizo o melhor cenário possível', icon: 'ri-lightbulb-line', profile: 'visionario' },
      { text: 'Analiso a situação com calma e lógica', icon: 'ri-brain-line', profile: 'guardiao' },
      { text: 'Busco novas perspectivas e soluções criativas', icon: 'ri-search-eye-line', profile: 'explorador' },
      { text: 'Procuro apoio e ofereço apoio aos outros', icon: 'ri-hand-heart-line', profile: 'curador' },
    ],
  },
  {
    id: 3,
    question: 'O que mais te motiva a escrever uma carta para o seu eu futuro?',
    options: [
      { text: 'Criar uma visão clara dos meus sonhos', icon: 'ri-star-line', profile: 'visionario' },
      { text: 'Registrar meu progresso e evolução', icon: 'ri-bar-chart-box-line', profile: 'guardiao' },
      { text: 'Descobrir coisas novas sobre mim mesmo', icon: 'ri-map-pin-user-line', profile: 'explorador' },
      { text: 'Expressar gratidão e amor-próprio', icon: 'ri-emotion-happy-line', profile: 'curador' },
    ],
  },
  {
    id: 4,
    question: 'Qual frase mais ressoa com você?',
    options: [
      { text: 'O impossível é apenas o que ainda não foi tentado', icon: 'ri-rocket-2-line', profile: 'visionario' },
      { text: 'A consistência é a mãe de todas as conquistas', icon: 'ri-medal-line', profile: 'guardiao' },
      { text: 'A vida começa onde termina a zona de conforto', icon: 'ri-road-map-line', profile: 'explorador' },
      { text: 'Curar a si mesmo é o primeiro passo para curar o mundo', icon: 'ri-plant-line', profile: 'curador' },
    ],
  },
  {
    id: 5,
    question: 'Como você prefere praticar o autoconhecimento?',
    options: [
      { text: 'Criando moodboards e visualizações do futuro', icon: 'ri-gallery-line', profile: 'visionario' },
      { text: 'Escrevendo diários e acompanhando hábitos', icon: 'ri-book-open-line', profile: 'guardiao' },
      { text: 'Viajando, lendo e experimentando coisas novas', icon: 'ri-global-line', profile: 'explorador' },
      { text: 'Meditando, terapia e conversas profundas', icon: 'ri-mental-health-line', profile: 'curador' },
    ],
  },
  {
    id: 6,
    question: 'Qual é o seu maior superpoder?',
    options: [
      { text: 'Inspirar pessoas com minha energia e visão', icon: 'ri-fire-line', profile: 'visionario' },
      { text: 'Ser confiável e manter a palavra', icon: 'ri-lock-line', profile: 'guardiao' },
      { text: 'Adaptar-me rapidamente a qualquer situação', icon: 'ri-refresh-line', profile: 'explorador' },
      { text: 'Sentir e compreender as emoções dos outros', icon: 'ri-empathize-line', profile: 'curador' },
    ],
  },
  {
    id: 7,
    question: 'Se pudesse receber uma mensagem do seu eu futuro, o que gostaria de ouvir?',
    options: [
      { text: 'Seus sonhos mais ousados se realizaram', icon: 'ri-trophy-line', profile: 'visionario' },
      { text: 'Você construiu uma base sólida e segura', icon: 'ri-home-heart-line', profile: 'guardiao' },
      { text: 'Você viveu experiências incríveis e únicas', icon: 'ri-flashlight-line', profile: 'explorador' },
      { text: 'Você tocou muitas vidas com seu amor', icon: 'ri-hearts-line', profile: 'curador' },
    ],
  },
];

export const quizProfiles: Record<string, QuizProfile> = {
  visionario: {
    id: 'visionario',
    title: 'O Visionário',
    subtitle: 'Arquiteto de Futuros',
    description: 'Você é uma pessoa que enxerga além do presente. Sua mente criativa e otimista transforma sonhos em planos concretos. Você inspira quem está ao redor com sua energia contagiante e sua capacidade de ver possibilidades onde outros veem obstáculos. Sua carta para o eu futuro será um mapa de estrelas — cheia de ambição, cor e propósito.',
    traits: [
      { icon: 'ri-lightbulb-flash-line', title: 'Criatividade Ilimitada', description: 'Sua mente é um laboratório de ideias brilhantes e soluções inovadoras' },
      { icon: 'ri-rocket-2-line', title: 'Ambição Positiva', description: 'Você sonha grande e tem a coragem de perseguir cada objetivo' },
      { icon: 'ri-magic-line', title: 'Poder de Manifestação', description: 'Sua capacidade de visualizar o futuro atrai as oportunidades certas' },
    ],
    recommendations: [
      'Escreva sua carta focando nos seus maiores sonhos — sem limites',
      'Use o Caderno da Gratidão para celebrar cada pequena vitória',
      'Compartilhe sua visão na Comunidade para inspirar outros',
    ],
    color: 'from-amber-500 to-gold-600',
    emoji: '✨',
  },
  guardiao: {
    id: 'guardiao',
    title: 'O Guardião',
    subtitle: 'Pilar de Força Interior',
    description: 'Você é a rocha que sustenta tudo ao redor. Sua disciplina, consistência e sabedoria emocional fazem de você alguém em quem todos confiam. Você valoriza o progresso gradual e sabe que grandes transformações nascem de pequenos hábitos diários. Sua carta será um testemunho de resiliência e crescimento constante.',
    traits: [
      { icon: 'ri-shield-star-line', title: 'Resiliência Inabalável', description: 'Você transforma desafios em degraus para o crescimento pessoal' },
      { icon: 'ri-timer-line', title: 'Disciplina Consistente', description: 'Seus hábitos diários constroem a fundação do seu sucesso' },
      { icon: 'ri-anchor-line', title: 'Estabilidade Emocional', description: 'Sua calma interior é um porto seguro para você e para os outros' },
    ],
    recommendations: [
      'Use a Carta do Eu Futuro para documentar sua evolução',
      'Ative os lembretes do Caderno da Gratidão para manter a consistência',
      'Explore os templates de cartas para estruturar seus objetivos',
    ],
    color: 'from-emerald-500 to-teal-600',
    emoji: '🛡️',
  },
  explorador: {
    id: 'explorador',
    title: 'O Explorador',
    subtitle: 'Desbravador de Caminhos',
    description: 'Você é movido pela curiosidade e pela sede de novas experiências. Cada dia é uma aventura e cada desafio é uma oportunidade de aprender algo novo. Você se reinventa constantemente e inspira outros a saírem da zona de conforto. Sua carta será um diário de descobertas e transformações surpreendentes.',
    traits: [
      { icon: 'ri-compass-discover-line', title: 'Curiosidade Infinita', description: 'Sua mente inquieta está sempre buscando novos horizontes e aprendizados' },
      { icon: 'ri-shuffle-line', title: 'Adaptabilidade Natural', description: 'Você floresce em meio às mudanças e abraça o inesperado' },
      { icon: 'ri-eye-line', title: 'Perspectiva Única', description: 'Você enxerga o mundo de ângulos que ninguém mais consegue ver' },
    ],
    recommendations: [
      'Escreva cartas para diferentes versões do seu eu futuro',
      'Explore a Galeria de Cartas para se inspirar com outras histórias',
      'Participe da Comunidade e descubra novas formas de autoconhecimento',
    ],
    color: 'from-orange-500 to-rose-500',
    emoji: '🧭',
  },
  curador: {
    id: 'curador',
    title: 'O Curador',
    subtitle: 'Guardião de Almas',
    description: 'Você possui o dom raro de sentir profundamente — tanto suas próprias emoções quanto as dos outros. Sua empatia e compaixão são forças transformadoras que curam e inspiram. Você entende que o autoconhecimento é um ato de amor. Sua carta será uma declaração de amor-próprio e um abraço no seu eu futuro.',
    traits: [
      { icon: 'ri-heart-3-line', title: 'Empatia Profunda', description: 'Você sente o mundo com uma intensidade que poucos conseguem compreender' },
      { icon: 'ri-hand-heart-line', title: 'Compaixão Ativa', description: 'Seu cuidado genuíno transforma a vida de quem está ao seu redor' },
      { icon: 'ri-seedling-line', title: 'Cura Interior', description: 'Você entende que curar a si mesmo é o caminho para curar o mundo' },
    ],
    recommendations: [
      'Use a Meditação Guiada antes de escrever sua carta',
      'Pratique a gratidão diária no Caderno da Gratidão',
      'Compartilhe seu depoimento para inspirar outros na jornada',
    ],
    color: 'from-rose-400 to-pink-500',
    emoji: '💜',
  },
};
